/**
 * Scoped leg actions of the Xaccel tab (phase 3): what the action bar counts, how a row button scopes its action, the
 * preview built from the server's `plan`, and "retry only the failed ones". No server is ever ticked by default and
 * no helper here starts an action: the panel calls plan, shows the preview, and only Confirm runs it.
 */
import type { LegStateRow, LegStateServer, LegPlanItem, LegActionResult, LegActionName } from '@/services/linear.service';

/** Actions of the action bar, in the order of its selector. */
export const BAR_ACTIONS: LegActionName[] = ['create', 'adopt', 'start', 'stop', 'restart', 'repair', 'rename', 'accept', 'delete', 'forget'];

/** Destructive or restarting actions: the preview button is red. */
export const DANGER_ACTIONS: LegActionName[] = ['stop', 'restart', 'delete', 'forget', 'repair', 'rename'];

/** Servers that can be ticked: the reachable ones (an unreachable server shows the reason and stays unticked). */
export function selectableServerIds(servers: LegStateServer[]): number[] {
  return servers.filter((s) => s.reachable).map((s) => s.id);
}

/** "Seleziona tutti i server": ticks every reachable server, or clears them when all are ticked. Selection only. */
export function toggleAllServers(servers: LegStateServer[], ticked: Set<number>): Set<number> {
  const ids = selectableServerIds(servers);
  const all = ids.length > 0 && ids.every((id) => ticked.has(id));
  return all ? new Set() : new Set(ids);
}

/** Rows of the ticked servers where `action` applies (row.actions from the server), and those servers' names. */
export function actionableCount(servers: LegStateServer[], ticked: Set<number>, action: LegActionName): { count: number; serverNames: string[] } {
  let count = 0;
  const names: string[] = [];
  for (const s of servers) {
    if (!ticked.has(s.id) || !s.reachable && action !== 'forget') continue;
    const n = s.rows.filter((r) => (r.actions ?? []).includes(action)).length;
    if (n > 0) { count += n; names.push(s.name); }
  }
  return { count, serverNames: names };
}

/** A stream named by its server ref; the name seen in the preview lets the server refuse a ref reused meanwhile. */
export type RefEntry = string | { ref: string; name: string };
export interface Scope { servers: number[]; legIds?: number[]; refs?: RefEntry[]; resolutions?: number[] }

/**
 * A row button acts on that row only: its DB leg (id), else its stream by server ref (orphan, stray copy, adoptable),
 * else (nothing exists yet: create) its resolution. Never a resolution for a row with a stream: on a renamed channel the
 * resolution also matches the old leg.
 */
export function rowScope(serverId: number, row: Pick<LegStateRow, 'resolutionId' | 'leg' | 'real'>): Scope {
  if (row.leg) return { servers: [serverId], legIds: [row.leg.id] };
  if (typeof row.real === 'object') return { servers: [serverId], refs: [row.real.ref] };
  return { servers: [serverId], resolutions: [row.resolutionId] };
}

/** legIds / refs / resolutions of a set of rows (see rowScope), empty lists left out. */
function namedScope(servers: number[], items: Array<{ legId?: number; ref?: string; resolutionId: number; legName: string }>): Scope {
  const legIds = [...new Set(items.filter((i) => i.legId).map((i) => i.legId!))];
  const seen = new Set<string>();
  const refs = items.filter((i) => !i.legId && i.ref && !seen.has(i.ref) && seen.add(i.ref)).map((i) => ({ ref: i.ref!, name: i.legName }));
  const resolutions = [...new Set(items.filter((i) => !i.legId && !i.ref).map((i) => i.resolutionId))].sort((a, b) => a - b);
  return { servers, ...(legIds.length ? { legIds } : {}), ...(refs.length ? { refs } : {}), ...(resolutions.length ? { resolutions } : {}) };
}

/** One line of the preview: the stream, what will be done to it, its check and real state. */
export interface PreviewEntry { name: string; op: string; verdict: string; realStatus: string; danger: boolean; untagged: boolean }

export interface ConfirmRow { serverId: number; name: string; type: string; running: number; stopped: number; items: string[]; entries: PreviewEntry[] }

/** Operations that stop, restart, delete, recreate, rename or forget something: shown in red in the preview. */
const DANGER_OPS = new Set(['delete-leg', 'delete-orphan', 'stop', 'restart', 'recreate', 'rename', 'forget']);

/** Preview rows (one per server with something to do) + the skipped items with their reason. */
export function planToConfirmRows(items: LegPlanItem[]): { rows: ConfirmRow[]; skipped: Array<{ server: string; name: string; reason: string }>; acting: number } {
  const rows: ConfirmRow[] = [];
  for (const i of items.filter((x) => x.act)) {
    let row = rows.find((r) => r.serverId === i.serverId);
    if (!row) { row = { serverId: i.serverId, name: i.serverName, type: i.serverType, running: 0, stopped: 0, items: [], entries: [] }; rows.push(row); }
    if (i.realStatus === 'running') row.running++;
    else if (i.realStatus === 'stopped' || i.realStatus === 'error') row.stopped++;
    row.items.push(i.newName ? `${i.legName} → ${i.newName}` : i.legName);
    row.entries.push({
      name: i.newName ? `${i.legName} → ${i.newName}` : i.legName,
      op: i.op || 'unknown',
      verdict: i.verdict,
      realStatus: i.realStatus,
      danger: DANGER_OPS.has(i.op || ''),
      untagged: !!i.untagged && i.op === 'adopt',
    });
  }
  const skipped = items.filter((x) => !x.act && !x.alreadyDone).map((x) => ({ server: x.serverName, name: x.legName, reason: x.skipped || 'not-applicable' }));
  return { rows, skipped, acting: items.filter((x) => x.act).length };
}

/**
 * Failed results worth retrying (errors and unreachable servers; never a conflict / not-applicable), named exactly:
 * their legs by id, their streams by ref, resolutions only for failed creates (nothing existed yet).
 */
export function retryScope(results: LegActionResult[]): Scope | null {
  const retryable = results.filter((r) => !r.ok && (!r.skipped || r.skipped === 'unreachable' || r.skipped === 'unknown-state'));
  if (retryable.length === 0) return null;
  return namedScope([...new Set(retryable.map((r) => r.serverId))].sort((a, b) => a - b), retryable);
}

export function resultSummary(results: LegActionResult[]): { done: number; alreadyDone: number; failed: number } {
  return {
    done: results.filter((r) => r.ok && !r.alreadyDone).length,
    alreadyDone: results.filter((r) => r.ok && r.alreadyDone).length,
    failed: results.filter((r) => !r.ok).length,
  };
}

/** DB legs on this server the channel no longer wants (deselected server, removed resolution) that can be deleted. */
export function notDesiredLegs(server: LegStateServer): LegStateRow[] {
  return server.rows.filter((r) => !r.desired && r.leg && (r.actions ?? []).includes('delete'));
}

const SKIP_KEYS: Record<string, string> = {
  unreachable: 'unreachable', 'unknown-state': 'unknownState', conflict: 'conflict', 'already-exists': 'alreadyExists',
  'already-running': 'alreadyRunning', 'already-stopped': 'alreadyStopped', 'already-gone': 'alreadyGone', 'needs-repair': 'needsRepair',
  'not-applicable': 'notApplicable', 'renamed-on-server': 'renamedOnServer', 'name-taken': 'nameTaken', 'not-supported': 'notSupported', 'stop-first': 'stopFirst', 'not-verified': 'notVerified', 'changed-since-preview': 'changedSincePreview',
};

export function skipReasonKey(reason: string): string {
  return `linear.legs.skip.${SKIP_KEYS[reason] ?? 'notApplicable'}`;
}

/**
 * Scopes of the confirmed run, narrowed to what the preview showed, ONE PER SERVER: legs by id, streams without a row
 * by ref, resolutions only for rows with neither (create). A row that appeared between the preview and the click is
 * never touched, and a destructive action is never sent by resolution alone (the server refuses it). [] = nothing to run.
 */
export function scopesFromPlan(items: LegPlanItem[]): Scope[] {
  const acting = items.filter((i) => i.act);
  const servers = [...new Set(acting.map((i) => i.serverId))].sort((a, b) => a - b);
  return servers.map((sid) => namedScope([sid], acting.filter((i) => i.serverId === sid)));
}

/** DB timestamp (UTC, "YYYY-MM-DD HH:MM:SS") in the viewer's local time with the time zone; '' when absent. */
export function formatLastActionAt(at: string | null | undefined, locale?: string, timeZone?: string): string {
  if (!at) return '';
  const d = new Date(`${at.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleString(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short', ...(timeZone ? { timeZone } : {}) });
}

/**
 * Requests of a confirmed run. Start / Restart naming only legs (ids are unique across servers) = ONE request for all
 * servers: one DRM key fetch, one restart-with-keys. Anything with refs (ids repeat across boxes) or resolutions, and
 * every other action, stays one request per server.
 */
export function runScopes(action: string, scopes: Scope[]): Scope[] {
  if ((action !== 'start' && action !== 'restart') || scopes.length < 2) return scopes;
  if (!scopes.every((s) => s.legIds && s.legIds.length > 0 && !s.refs && !s.resolutions)) return scopes;
  return [{ servers: [...new Set(scopes.flatMap((s) => s.servers))].sort((a, b) => a - b), legIds: scopes.flatMap((s) => s.legIds!) }];
}

/** Actions shown DISABLED on a row, with the reason: X Accel rename until its partial /update {name} is verified live. */
export function disabledRowActions(serverType: string, row: Pick<LegStateRow, 'verdict'>): Array<{ action: LegActionName; reasonKey: string }> {
  return serverType === 'xaccel' && row.verdict === 'stale-name' ? [{ action: 'rename', reasonKey: 'linear.legs.renameNotVerified' }] : [];
}
